import { useCallback, useEffect, useMemo, useState } from 'react';
import { GameStatus, GameTypes, NewGameTypes, PopUpTypes } from '@/types/game';
import commonUtils from '@/utils/common';
import { Transition, TransitionStatus } from 'react-transition-group';
import classes from 'classnames';
import classNames from './QuizView.module.scss';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { interfaceLanguageSelector } from '@/store/reducers/auth';
import UseScreenSize from '@/hooks/UseScreenSize';
import { SkillTypes } from '@/types/skill';
import UseLocalLang from '@/hooks/UseLocalLang';

import AppModal from '@/components/AppModal';
import QuizPageControls from '@/components/QuizPage/QuizPageControls/QuizPageControls';
import QuizDetails from '@/components/QuizPage/QuizDetails/QuizDetails';
import QuizQuestion from '@/components/QuizPage/QuizQuestion/QuizQuestion';
import QuizPageIndicators from '@/components/QuizPage/QuizPageIndicators/QuizPageIndicators';
import QuizPopUp from '@/components/QuizPage/QuizPopUp/QuizPopUp';
import AppBackdrop from '@/components/AppBackdrop';
import QuizPreparation from '@/components/QuizPage/QuizPreparation/QuizPreparation';
import QuizLanguageToggler from '@/components/QuizPage/QuizLanguageToggler/QuizLanguageToggler';
import WordsList from '@/components/QuizPage/QuizPopUp/PopUpWordsList/WordsList/WordsList';
import AppLoader from '@/components/AppLoader';
import AppBanner from '@/components/AppBanner';
import { gameSelector, gameActions } from '@/store/reducers/game';
import LevelTestLogoutComponent from '../level-test/LevelTestLogoutComponent';
import { TopicTypes } from '@/types/topic';
import ReadPreparation from '@/components/QuizPage/QuizPreparation/ReadPreparation/ReadPreparation';
import { useDispatch } from 'react-redux';
import { UseQuizInterface } from './UseQuiz';
import TutorFromActivitise from '@/components/TutorFromActivitise/TutorFromActivitise';
import { authSelector } from '@/store/reducers/auth';
import { unitSelector } from '@/store/reducers/unit';

const QuizViewDesktop = ({ quizData }: { quizData: UseQuizInterface }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector(authSelector);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { selectedUnit } = useAppSelector(unitSelector);

  const {
    loadQuizProgress,
    currentQuestion,
    selectedTopic,
    selectedQuiz,
    selectedSkill,
    selectedType,
    gameStatus,
    currentQuestionIndex,
    currentReadQuestionIndex,
    gameQuestions,
    gameTypeReal,
    coins,
    currentTime,
    currentCoins,
    shouldDisplayPopUp,
    popUpType,
    popUpProps,
    isMuted,
    allowRetry,
    showPreQuiz,
    onMute,
    onPause,
    onStartQuiz,
    onPractice,
    onDisableRetry,
    onClosePopUp,
    onAknowledge,
    onSwipe,
    toggleCheckAnswer,
    goToNextQuestion,
    currentQuestionTempData,
    setCurrentQuestionTempData,
  } = quizData;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(gameActions.setPauseTimer(isChatOpen));
  }, [isChatOpen]);

  useEffect(() => {
    if (!currentQuestion?.gameType) {
      return;
    }

    if (
      gameTypeReal === GameTypes.Practice &&
      currentQuestion.gameType === NewGameTypes.Vocabulary
    ) {
      return;
    }

    dispatch(gameActions.setGameType(currentQuestion.gameType));
  }, [currentQuestion, gameTypeReal]);

  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);

  const { largeWidth, windowWidth, breakpointDesktop } = UseScreenSize();
  const isInEndOfSkillTest = selectedTopic?.topicType === TopicTypes.EndOfSkillTest;

  const onAnswer = (userAnswer: string) => {
    toggleCheckAnswer(userAnswer);
  };

  const setClasseByPopType = useCallback(
    (state: TransitionStatus) => {
      return popUpType === PopUpTypes.WordsList
        ? `slide-horizontal-${interfaceLanguage?.direction}-${state}`
        : `fade-${state}`;
    },
    [popUpType]
  );

  const onGoBack = () => {
    return navigate('/dashboard/games');
  };

  const isPractice = gameTypeReal === GameTypes.Practice;

  const { isLevelTest, isFinalAssessment } = useAppSelector(gameSelector);
  //FIXME: NEED TO REPLACE WITH CORRECT GAMETYPE
  const TEMP_GAMETYPE = gameQuestions[0]?.gameType as any;
  const isRead = TEMP_GAMETYPE === NewGameTypes.Read;
  const isSpeak = selectedType === SkillTypes.Speak;
  const localLang = UseLocalLang();

  const handleChatToggle = (isOpen: boolean) => {
    setIsChatOpen(isOpen);
  };

  const showTutor = useMemo(() => {
    return user?.metadata?.organizationDetails?.tutorData?.isTutorActive && !isLevelTest;
  }, [user, isLevelTest]);

  const tutorInstance = useMemo(() => {
    const selectedLevel = localStorage.getItem('selectedLevel');
    const Parselevel = selectedLevel ? parseInt(selectedLevel, 10) : 1;
    const level = user?.progress?.userLevel || Parselevel;

    if (!selectedTopic || !selectedQuiz) {
      return <></>;
    }

    return (
      <TutorFromActivitise
        onChatToggle={handleChatToggle}
        level={level}
        unit={selectedUnit}
        topic={selectedTopic}
        activity={selectedQuiz}
      />
    );
  }, [selectedUnit, selectedQuiz, selectedTopic, user]);

  const quizQuestion = (
    <QuizQuestion
      loading={loadQuizProgress}
      currentQuestion={currentQuestion}
      allowRetry={allowRetry}
      emitOnAnswer={onAnswer}
      emitOnRetry={onDisableRetry}
      emitOnNextQuestion={goToNextQuestion}
      emitOnSwipe={onSwipe}
      emitOnAknowledge={onAknowledge}
      currentQuestionTempData={currentQuestionTempData}
      setCurrentQuestionTempData={setCurrentQuestionTempData}
    />
  );

  const quizControls = (
    <QuizPageControls
      actual={currentQuestionIndex || currentQuestionIndex === 0 ? currentQuestionIndex + 1 : 0}
      total={gameQuestions.length}
      onMute={onMute}
      onPause={onPause}
      isMuted={isMuted}
      isPaused={gameStatus === GameStatus.PAUSED}
      loading={loadQuizProgress}
    />
  );

  const popUpComponent = (
    <Transition
      in={Boolean(shouldDisplayPopUp && popUpType && popUpProps)}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop
              className={classes(classNames.backdrop, `backdrop-${state}`, {
                [classNames.backdropWhite]:
                  popUpType === PopUpTypes.Focus || popUpType === PopUpTypes.KeepGoing,
              })}
              emitClose={() => {
                if (popUpProps?.canClickOutside === false) {
                  return;
                }

                if (popUpType !== PopUpTypes.Summary && popUpType !== PopUpTypes.TimeIsUp) {
                  onClosePopUp();
                  return;
                }
              }}
            />
            <AppModal>
              <QuizPopUp
                {...popUpProps}
                className={classes(classNames.modal, setClasseByPopType(state))}
                type={popUpType}
                tip={commonUtils.getQuestionTip(currentQuestion ?? {}, localLang) ?? ''}
              />
            </AppModal>
          </>
        );
      }}
    />
  );

  const levelTestLogoutComponent = (
    <LevelTestLogoutComponent className={classNames.levelTestSignOut} />
  );

  if (loadQuizProgress) {
    return <AppLoader />;
  }

  let content = (
    <div className='flex flex-1 justify-between gap-8 px-11'>
      <div className={classes(classNames.quizDesktopContent)}>
        {quizControls}
        {!isFinalAssessment && (
          <div className={classNames.detailsAndIndicators}>
            <QuizDetails
              selectedType={selectedType}
              selectedTopic={selectedTopic}
              selectedQuiz={selectedQuiz}
              shouldDisplayImage={true}
            />
            {isPractice ? (
              <QuizLanguageToggler />
            ) : (
              !isInEndOfSkillTest && (
                <QuizPageIndicators
                  earnedCoins={coins}
                  timesUp={!loadQuizProgress && currentTime <= 5}
                  coins={currentCoins}
                  timer={currentTime ?? 0}
                  selectedType={selectedType}
                />
              )
            )}
          </div>
        )}
        {quizQuestion}
        {showTutor && tutorInstance}
      </div>
      {popUpComponent}
      {isPractice && breakpointDesktop <= windowWidth && (
        <WordsList emitOnAknowledge={onAknowledge} />
      )}
    </div>
  );

  //FIXME: Need to remove, old functionality
  // const showPreparation =
  //   (!isInEndOfSkillTest && (showPreQuiz || isRead)) ||
  //   (isInEndOfSkillTest && !showPreQuiz && isRead);

  //FIXME: Need to use correct condition based on new functionality
  const showPreparation = showPreQuiz;

  if (showPreparation) {
    content = (
      <>
        <div className='px-11 py-3 flex-1 overflow-x-hidden overflow-y-auto'>
          {isLevelTest && levelTestLogoutComponent}
          <QuizPreparation
            currentQuestion={
              isLevelTest ? gameQuestions[currentReadQuestionIndex] : currentQuestion
            }
            currentQuestionIndex={isLevelTest ? currentReadQuestionIndex : currentQuestionIndex}
            gameQuestions={gameQuestions}
            loading={loadQuizProgress}
            selectedQuiz={selectedQuiz}
            selectedSkill={selectedSkill}
            selectedTopic={selectedTopic}
            selectedType={selectedType}
            coins={coins}
            currentCoins={currentCoins}
            currentTime={currentTime}
            isPaused={gameStatus === GameStatus.PAUSED}
            emitOnStartQuiz={onStartQuiz}
            emitGoBack={onGoBack}
            emitOnPractice={onPractice}
            emitPause={onPause}
          />
        </div>
        {popUpComponent}
      </>
    );
  }

  if (isLevelTest && !isRead) {
    content = (
      <>
        {isLevelTest && levelTestLogoutComponent}
        {quizQuestion}
        {showTutor && tutorInstance}
        {popUpComponent}
      </>
    );
  }

  //FIXME: Need to try to put it in content variable (starts from line 191)
  if (isRead && !showPreparation) {
    content = (
      <>
        <ReadPreparation
          currentQuestion={isLevelTest ? gameQuestions[currentReadQuestionIndex] : currentQuestion}
          currentQuestionIndex={isLevelTest ? currentReadQuestionIndex : currentQuestionIndex}
          gameQuestions={gameQuestions}
          loading={loadQuizProgress}
          selectedQuiz={selectedQuiz}
          selectedSkill={selectedSkill}
          selectedTopic={selectedTopic}
          selectedType={selectedType}
          coins={coins}
          currentCoins={currentCoins}
          currentTime={currentTime}
          isPaused={gameStatus === GameStatus.PAUSED}
          emitOnStartQuiz={onStartQuiz}
          emitGoBack={onGoBack}
          emitPause={onPause}
          shouldDisplayPopUp={shouldDisplayPopUp}
          popUpType={popUpType}
        />
        {showTutor && tutorInstance}
        {popUpComponent}
      </>
    );
  }

  return (
    <div
      className={classes(classNames.quizDesktop, {
        'flex-1': isSpeak && showPreQuiz,
        'h-full': !(isSpeak && showPreQuiz),
        [classNames.quizDesktopLevelTest]: isLevelTest,
        [classNames.quizDesktopFinalAssessment]: isFinalAssessment,
        [classNames.quizDesktopPractice]: isPractice,
      })}
    >
      {!isLevelTest && !isFinalAssessment && (
        // <TheHeader
        //   onSearch={onSearch}
        //   onInputChange={onInputChange}
        //   searchValue={searchValue}
        // />
        <AppBanner className={classNames.appBanner} />
      )}
      {
        //TODO: Need to understand if it's needed
        /* {isSearching ? (
        <AppLoaderCircle className="m-auto" />
      ) : selectedWord ? (
        <div className={classNames.quizDictionaryWrapper}>
          {dictionaryComponents}
        </div>
      ) : (
        content
      )} */
      }
      {content}
    </div>
  );
};

export default QuizViewDesktop;
